import { useApolloClient } from "@apollo/client";
import {
  type AddInboxConversationLabelMutation,
  type FetchResult,
  type InboxConversationsConversationFragment,
  InboxConversationsConversationFragmentDoc,
  InboxConversationsDocument,
  type InboxConversationsQuery,
  type MutationResult,
  useAddInboxConversationLabelMutation,
} from "@qw/graphql";
import { KnownInboxLabel } from "@qw/qw-common";
import { useCallback } from "react";

import type { MaybePartialInboxConversation } from "../types";
import { useInboxFilter } from "./inbox-filter-provider";

export type UseAddLabelToConversationReturn = [
  (
    conversation: MaybePartialInboxConversation,
    label: string
  ) => Promise<FetchResult<AddInboxConversationLabelMutation> | undefined>,
  MutationResult<AddInboxConversationLabelMutation>,
];

export const useAddLabelToConversation = (): UseAddLabelToConversationReturn => {
  const { filters: inboxFilter } = useInboxFilter();
  const apolloClient = useApolloClient();
  const [addLabelMutation, result] = useAddInboxConversationLabelMutation({
    refetchQueries: ["InboxConversations", "InboxLabelUnreadCounts"],
  });

  const addLabel = useCallback(
    async (conversation: MaybePartialInboxConversation, label: string) => {
      const isMovingToTrash = !inboxFilter.labels?.includes(KnownInboxLabel.TRASH) && label === KnownInboxLabel.TRASH;
      const isMovingFromTrash =
        inboxFilter.labels?.includes(KnownInboxLabel.TRASH) &&
        ![KnownInboxLabel.TRASH, KnownInboxLabel.STARRED, KnownInboxLabel.UNREAD].includes(label as KnownInboxLabel);
      const cacheId = apolloClient.cache.identify({ __typename: "Conversation", id: conversation.id });
      const cachedConversation = cacheId
        ? apolloClient.cache.readFragment<InboxConversationsConversationFragment>({
            id: cacheId,
            fragment: InboxConversationsConversationFragmentDoc,
          })
        : null;
      const attachmentPreviews =
        "attachmentPreviews" in conversation
          ? conversation.attachmentPreviews
          : (cachedConversation?.attachmentPreviews ?? []);
      const draftCount = "draftCount" in conversation ? conversation.draftCount : (cachedConversation?.draftCount ?? 0);
      const messageCount =
        "messageCount" in conversation ? conversation.messageCount : (cachedConversation?.messageCount ?? 0);

      return addLabelMutation({
        variables: { input: { conversationId: conversation.id, label } },
        update(cache, { data }) {
          const conversation = data?.addInboxConversationLabel;

          if (!conversation) {
            return;
          }

          const existingData: InboxConversationsQuery | null = cache.readQuery({
            query: InboxConversationsDocument,
            variables: { input: inboxFilter },
          });

          const existingConversations = existingData?.inboxConversations.conversations;

          if (!existingConversations) {
            return;
          }

          const shouldRemoveFromCache = isMovingToTrash || isMovingFromTrash;

          if (shouldRemoveFromCache) {
            cache.writeQuery({
              query: InboxConversationsDocument,
              variables: { input: inboxFilter },
              data: {
                inboxConversations: {
                  ...existingData.inboxConversations,
                  conversations: existingConversations?.filter(
                    (updatedConversation) => updatedConversation.id !== conversation.id
                  ),
                },
              },
            });
          }
        },
        optimisticResponse: () => {
          const labelsSet = new Set(conversation.labels);

          if (isMovingToTrash) {
            labelsSet.delete("INBOX");
          }
          if (isMovingFromTrash) {
            labelsSet.delete("TRASH");
          }
          labelsSet.add(label);

          return {
            addInboxConversationLabel: {
              ...(cachedConversation ?? {}),
              ...conversation,
              attachmentPreviews,
              draftCount,
              messageCount,
              labels: [...labelsSet],
            },
          };
        },
      });
    },
    [addLabelMutation, inboxFilter, apolloClient]
  );

  return [addLabel, result];
};
